/* Shop area of the collection page (sections/main-collection-product-grid.liquid) and the search results page
   (sections/main-search.liquid), both built on snippets/collection-filters.liquid.
   - Instant apply: filter/sort changes fetch the section (id from #ColShop) through the Section Rendering API and
     swap every [data-col-swap] element on the page by id (search: also the hero count); the URL is pushed without
     section_id. The query is the form's own fields (search: hidden q, type, options[prefix] and the Equipment
     values) plus the sort. Back/forward refetch.
   - Price slider (unnamed range inputs, never submitted): dragging writes the price boxes, release applies through
     the matching box; typing a price moves its handle.
   - Phone bottom sheet: dialog semantics while open, focus trap, Esc/scrim close, body scroll lock.
   - Expert band: "Online now" when the visitor's time is inside support_hours (same rule as assets/header.js).
   - [data-open-chat] opens Tidio; in the expert band it opens on the 'viewed' (data-chat-models) or 'collection' question.
   Events are delegated from document, so swapped markup needs no rebinding.
   Without this file the form submits as GET and every chip/pagination link is a normal link.
   Collection grid only (#product-grid[data-grid], cards from home-product-card grid: true; the search page has none
   of this markup, so these steps do nothing there):
   - Load more (a[data-col-more] in #ColPages): fetches the next page through the Section Rendering API, appends its
     cards (not the expert band) minus twins already shown (data-pid / data-sibs), swaps #ColPages and rewrites its
     note and bar from the cards in the grid (data-total, data-tpl-*); focus to the first new card, count announced.
     Loaded pages are kept in sessionStorage per path + query, so Back from a product reloads them and restores the
     scroll position; any filter/sort swap clears them. On failure the link is followed. A click on the note or bar
     (.col-more__note / .col-more__bar) runs the same path while the link exists.
   - One card per model after every swap (same rule as Load more), then window.cfsCards.refresh on the grid
     (assets/product-card.js: ship day, label fit, row labels, width watch). Swatch clicks, the 5-minute ship-day
     update and the font/visibility refits are product-card.js's own.
   Also on collection pages (each runs only when its markup is there, before the #ColShop check):
   - Hero description toggle (snippets/collection-hero.liquid, [data-desc-toggle] next to #ColHeroDesc; output only
     when the collection has no buying guide): shows "Read more" when the 2-line clamp hides text, toggles it.
   - Buying guide (snippets/collection-guide.liquid, #ColGuide): clamp, Read more/less, and opening it from links to
     #ColGuide (the hero's "Read more") or #ColGuide in the URL. */
(() => {
  /* ---------- Collection hero: description toggle ---------- */
  (() => {
    const desc = document.getElementById('ColHeroDesc');
    const toggle = desc && desc.parentNode.querySelector('[data-desc-toggle]');
    if (!toggle) return;

    function refresh() {
      if (desc.classList.contains('is-expanded') || desc.scrollHeight > desc.clientHeight + 2) {
        toggle.hidden = false;
      } else {
        toggle.hidden = true;
      }
    }

    toggle.addEventListener('click', () => {
      const expanded = desc.classList.toggle('is-expanded');
      toggle.textContent = expanded ? toggle.dataset.less : toggle.dataset.more;
      toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });

    // Wait a tick for fonts/layout to settle before measuring overflow.
    requestAnimationFrame(refresh);
    window.addEventListener('resize', refresh, { passive: true });
  })();

  /* ---------- Collection buying guide: clamp + Read more/less ---------- */
  (() => {
    const guide = document.getElementById('ColGuide');
    if (!guide) return;
    const body = guide.querySelector('.col-guide__body');
    const btn = guide.querySelector('.col-guide__toggle');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    guide.classList.add('is-clamped');
    if (body.scrollHeight <= body.clientHeight + 1) {
      guide.classList.remove('is-clamped');
      return;
    }
    btn.hidden = false;

    btn.addEventListener('click', () => {
      const clamped = guide.classList.toggle('is-clamped');
      btn.textContent = clamped ? btn.dataset.more : btn.dataset.less;
      btn.setAttribute('aria-expanded', clamped ? 'false' : 'true');
      if (clamped && guide.getBoundingClientRect().top < 0) {
        guide.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
      }
    });

    // Open from "Read more" links to #ColGuide (e.g. the collection hero) and from #ColGuide in the URL.
    // The browser's normal anchor jump still happens.
    function expand() {
      if (!guide.classList.contains('is-clamped')) return;
      guide.classList.remove('is-clamped');
      btn.textContent = btn.dataset.less;
      btn.setAttribute('aria-expanded', 'true');
    }
    document.addEventListener('click', (e) => {
      const link = e.target.closest && e.target.closest('a[href$="#ColGuide"]');
      if (link) expand();
    });
    if (window.location.hash === '#ColGuide') expand();
  })();

  /* ---------- Shop area ---------- */
  const shop = document.getElementById('ColShop');
  if (!shop) return;

  const sectionId = shop.dataset.sectionId;
  const phone = window.matchMedia('(max-width: 749px)');
  const DEBOUNCE = 300;
  let timer = null;
  let controller = null;
  let requestId = 0;
  let pendingTrigger = null;

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const form = () => document.getElementById('ColFilterForm');
  const results = () => document.getElementById('ColResults');
  const baseUrl = () => (form() ? form().getAttribute('action') : window.location.pathname);

  /* ---------- Query building ---------- */
  const PRICE = /^filter\.v\.price\.(gte|lte)$/;

  function normalisePrice(value) {
    const v = value.replace(/\s/g, '');
    // "1,500.00" → "1500.00" (thousands separators only)
    return /^\d{1,3}(,\d{3})+(\.\d+)?$/.test(v) ? v.replace(/,/g, '') : v;
  }

  function buildQuery() {
    const params = new URLSearchParams();
    const f = form();
    if (f) {
      for (const [key, raw] of new FormData(f).entries()) {
        if (key === 'sort_by') continue; // added once below from the select
        let value = String(raw).trim();
        if (PRICE.test(key)) value = normalisePrice(value);
        if (value === '') continue;
        params.append(key, value);
      }
    }
    const sort = $('[data-col-sort]');
    if (sort && sort.value) params.set('sort_by', sort.value);
    return params.toString();
  }

  function urlFromForm() {
    const q = buildQuery();
    return baseUrl() + (q ? '?' + q : '');
  }

  /* ---------- Fetch + swap ---------- */
  function triggerKey(el) {
    if (!el || !el.name) return null;
    return { name: el.name, value: el.type === 'checkbox' ? el.value : null };
  }

  function findByKey(key) {
    if (!key) return null;
    const candidates = $$('[name]', shop).filter((el) => el.name === key.name);
    if (key.value === null) return candidates[0] || null;
    return candidates.find((el) => el.value === key.value) || null;
  }

  function headerOffset() {
    const hdr = $('[data-hdr-sticky]');
    let off = hdr && hdr.classList.contains('is-stuck') ? hdr.offsetHeight : 0;
    if (phone.matches) {
      const bar = document.getElementById('ColToolbar');
      if (bar) off += bar.offsetHeight;
    }
    return off + 16;
  }

  function scrollToGrid() {
    const grid = document.getElementById('product-grid');
    if (!grid) return;
    const top = grid.getBoundingClientRect().top;
    const off = headerOffset();
    if (top < off) {
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      window.scrollTo({ top: window.scrollY + top - off, behavior: reduce ? 'auto' : 'smooth' });
    }
  }

  function setBusy(on) {
    const r = results();
    if (!r) return;
    if (on) r.setAttribute('aria-busy', 'true');
    else r.removeAttribute('aria-busy');
  }

  function load(url, opts = {}) {
    if (controller) controller.abort();
    controller = new AbortController();
    const id = ++requestId;
    setBusy(true);

    const fetchUrl = url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(sectionId);
    fetch(fetchUrl, { signal: controller.signal, headers: { Accept: 'text/html' } })
      .then((res) => {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then((html) => {
        if (id !== requestId) return;
        swap(html, opts);
        if (opts.push) history.pushState({ colShop: true }, '', url);
        setBusy(false);
      })
      .catch((err) => {
        if (err && err.name === 'AbortError') return;
        // Section fetch failed: fall back to a normal page load of the same URL
        window.location.href = url;
      });
  }

  function swap(html, opts) {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    // Remember what the swap would lose: open groups, sheet scroll, focus
    const openState = {};
    $$('#ColFilters details[data-facet]').forEach((d) => { openState[d.dataset.facet] = d.open; });
    const body = $('[data-col-sheet-body]');
    const bodyScroll = body ? body.scrollTop : 0;
    const active = document.activeElement;
    const focusKey = opts.trigger || (active && shop.contains(active) ? triggerKey(active) : null);
    const hadFocusInShop = active && shop.contains(active);
    // A slider handle has no name: remember which one, so arrow-key use keeps focus on it after the swap
    const rangeSel = active && active.matches && active.matches(RANGE)
      ? (active.hasAttribute('data-col-range-min') ? '[data-col-range-min]' : '[data-col-range-max]')
      : null;

    $$('[data-col-swap]').forEach((el) => {
      const next = doc.getElementById(el.id);
      if (next) el.replaceWith(document.importNode(next, true));
    });

    $$('#ColFilters details[data-facet]').forEach((d) => {
      if (Object.prototype.hasOwnProperty.call(openState, d.dataset.facet)) d.open = openState[d.dataset.facet];
    });
    if (body) body.scrollTop = bodyScroll;

    // Sheet open state lives on the (unswapped) sheet; the new Filter button needs its aria-expanded back
    const sheetOpen = sheet() && sheet().classList.contains('is-open');
    const opener = $('[data-col-sheet-open]');
    if (opener) opener.setAttribute('aria-expanded', sheetOpen ? 'true' : 'false');

    // Focus: the same input if it still exists, otherwise the results area
    if (hadFocusInShop || opts.trigger) {
      const target = (rangeSel && $('#ColFilters ' + rangeSel)) || findByKey(focusKey);
      if (target && !target.disabled) target.focus({ preventScroll: true });
      else if (sheetOpen) { const close = $('.col-sheet__close'); if (close) close.focus({ preventScroll: true }); }
      else {
        const r = results();
        if (r) { r.setAttribute('tabindex', '-1'); r.focus({ preventScroll: true }); }
      }
    }

    // New results start at page 1: forget loaded Load more pages; one card per model, ship day, label fit
    clearMore();
    afterGrid();

    // Announce the new count
    const live = $('[data-col-live]');
    const count = $('.col-toolbar__count');
    if (live && count) live.textContent = count.textContent.trim();

    updateOnline();

    if (opts.scroll === 'always' || (opts.scroll && !phone.matches)) scrollToGrid();
  }

  function schedule(trigger) {
    pendingTrigger = triggerKey(trigger);
    clearTimeout(timer);
    timer = setTimeout(() => {
      load(urlFromForm(), { push: true, scroll: true, trigger: pendingTrigger });
    }, DEBOUNCE);
  }

  function applyNow(trigger) {
    clearTimeout(timer);
    load(urlFromForm(), { push: true, scroll: true, trigger: triggerKey(trigger) });
  }

  /* ---------- Price slider ([data-col-range] above the min/max boxes, same .col-facet) ---------- */
  const RANGE = '[data-col-range-min], [data-col-range-max]';

  function rangeParts(el) {
    const facet = el.closest('.col-facet');
    const wrap = facet && $('[data-col-range]', facet);
    if (!wrap) return null;
    const texts = $$('[data-col-price]', facet);
    const lo = $('[data-col-range-min]', wrap);
    const hi = $('[data-col-range-max]', wrap);
    if (!lo || !hi) return null;
    return { wrap, lo, hi, loText: texts[0], hiText: texts[1], max: parseFloat(hi.max) || 0 };
  }

  function paintRange(p) {
    const lo = p.max ? (+p.lo.value / p.max) * 100 : 0;
    const hi = p.max ? (+p.hi.value / p.max) * 100 : 0;
    const track = $('.col-range__track', p.wrap);
    if (track) {
      track.style.setProperty('--lo', lo + '%');
      track.style.setProperty('--hi', hi + '%');
    }
    // Both handles at the top end: the min handle must be the one on top, or it can't be dragged back
    p.wrap.classList.toggle('is-lo-top', lo > 50);
  }

  // Box text for a handle: plain whole dollars, empty at the ends (no filter)
  function writePriceText(p, isMin) {
    const v = +(isMin ? p.lo : p.hi).value;
    const box = isMin ? p.loText : p.hiText;
    if (box) box.value = (isMin ? v > 0 : v < p.max) ? String(v) : '';
  }

  // Dragging: keep min ≤ max by pushing the other handle, write the moved value(s) into the boxes
  function onRangeInput(el) {
    const p = rangeParts(el);
    if (!p) return;
    const isMin = el === p.lo;
    if (+p.lo.value > +p.hi.value) {
      if (isMin) p.hi.value = p.lo.value;
      else p.lo.value = p.hi.value;
      writePriceText(p, !isMin);
    }
    writePriceText(p, isMin);
    paintRange(p);
  }

  // Typed price: move the matching handle (clamped to the range and the other handle)
  function syncRangeFromText(box) {
    const p = rangeParts(box);
    if (!p) return;
    const isMin = box === p.loText;
    const n = parseFloat(normalisePrice(box.value.trim()));
    let v = isNaN(n) ? (isMin ? 0 : p.max) : Math.min(Math.max(n, 0), p.max);
    if (isMin) { v = Math.min(v, +p.hi.value); p.lo.value = String(v); }
    else { v = Math.max(v, +p.lo.value); p.hi.value = String(v); }
    paintRange(p);
  }

  /* ---------- Phone sheet ---------- */
  const sheet = () => document.getElementById('ColSheet');
  const panel = () => $('[data-col-sheet-panel]');

  function openSheet() {
    const s = sheet();
    const p = panel();
    if (!s || !p) return;
    p.setAttribute('role', 'dialog');
    p.setAttribute('aria-modal', 'true');
    s.classList.add('is-open');
    document.documentElement.classList.add('col-sheet-lock');
    const opener = $('[data-col-sheet-open]');
    if (opener) opener.setAttribute('aria-expanded', 'true');
    const close = $('.col-sheet__close', p);
    if (close) close.focus({ preventScroll: true });
  }

  function closeSheet() {
    const s = sheet();
    const p = panel();
    if (!s || !s.classList.contains('is-open')) return;
    s.classList.remove('is-open');
    if (p) { p.removeAttribute('role'); p.removeAttribute('aria-modal'); }
    document.documentElement.classList.remove('col-sheet-lock');
    const opener = $('[data-col-sheet-open]');
    if (opener) { opener.setAttribute('aria-expanded', 'false'); opener.focus({ preventScroll: true }); }
  }

  function trapFocus(e) {
    const p = panel();
    const focusables = $$('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])', p)
      .filter((el) => el.getClientRects().length > 0 && !el.closest('details:not([open]) > :not(summary)'));
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && (document.activeElement === first || !p.contains(document.activeElement))) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && (document.activeElement === last || !p.contains(document.activeElement))) {
      e.preventDefault(); first.focus();
    }
  }

  /* ---------- Collection grid ([data-grid]; none of this exists on the search page) ---------- */
  const grid = () => document.querySelector('#product-grid[data-grid]');
  const cards = (g) => $$(':scope > li.hc-card', g);

  // One card per model: drop a card whose product, or one of its colour siblings, is already earlier in the grid
  function dedupe(g) {
    const seen = new Set();
    cards(g).forEach((li) => {
      const pid = li.dataset.pid;
      if (!pid) return;
      const sibs = (li.dataset.sibs || '').split(',').filter(Boolean);
      if (seen.has(pid) || sibs.some((id) => seen.has(id))) li.remove();
      else seen.add(pid);
    });
  }

  // One card per model, then ship day, label fit and row labels (assets/product-card.js) on the grid
  function afterGrid() {
    const g = grid();
    if (!g) return;
    dedupe(g);
    window.cfsCards && window.cfsCards.refresh(g);
    document.dispatchEvent(new CustomEvent('cfs:grid-updated'));
  }

  /* ---------- Load more ---------- */
  // Loaded pages per URL (path + query) for this tab, so Back from a product restores them and the scroll position
  const MORE_KEY = 'colMore:';
  const moreKey = () => MORE_KEY + window.location.pathname + window.location.search;
  function readMore() {
    try { return JSON.parse(sessionStorage.getItem(moreKey()) || 'null'); } catch (e) { return null; }
  }
  function writeMore(v) {
    try { sessionStorage.setItem(moreKey(), JSON.stringify(v)); } catch (e) { /* storage off */ }
  }
  function clearMore() {
    try {
      Object.keys(sessionStorage).filter((k) => k.indexOf(MORE_KEY) === 0).forEach((k) => sessionStorage.removeItem(k));
    } catch (e) { /* storage off */ }
  }

  // Rewrite the note and bar with the cards actually in the grid (only when the model total is known)
  function updateMoreNote() {
    const box = $('#ColPages .col-more');
    const g = grid();
    if (!box || !g) return;
    const total = parseInt(box.dataset.total, 10);
    if (!total) return;
    const shown = cards(g).length;
    const note = $('.col-more__note', box);
    const bar = $('.col-more__bar i', box);
    const all = shown >= total;
    if (note) {
      const text = all
        ? (box.dataset.tplAll || '').replace('__count__', total)
        : (box.dataset.tplShowing || '').replace('__shown__', shown).replace('__count__', total);
      if (text) note.textContent = text;
    }
    if (bar) bar.style.width = (all ? 100 : Math.floor((shown / total) * 100)) + '%';
  }

  let moreBusy = false;
  // Fetches the next page, appends its cards (never the expert band) minus twins already shown, swaps #ColPages.
  // Resolves with the appended cards; rejects on failure.
  function loadMore(link) {
    const g = grid();
    if (!g || moreBusy) return Promise.reject(new Error('busy'));
    moreBusy = true;
    const gen = requestId;
    link.setAttribute('aria-busy', 'true');
    const url = link.href;
    const fetchUrl = url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(sectionId);
    return fetch(fetchUrl, { headers: { Accept: 'text/html' } })
      .then((res) => {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then((html) => {
        moreBusy = false;
        // A filter/sort swap happened meanwhile: this page belongs to the old results
        if (gen !== requestId || grid() !== g) return null;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const seen = new Set(cards(g).map((li) => li.dataset.pid));
        const added = [];
        doc.querySelectorAll('#product-grid > li.hc-card').forEach((li) => {
          const pid = li.dataset.pid;
          const sibs = (li.dataset.sibs || '').split(',').filter(Boolean);
          if (!pid || seen.has(pid) || sibs.some((id) => seen.has(id))) return;
          seen.add(pid);
          const node = document.importNode(li, true);
          g.appendChild(node);
          added.push(node);
        });
        const nextPages = doc.getElementById('ColPages');
        const pages = document.getElementById('ColPages');
        if (nextPages && pages) pages.replaceWith(document.importNode(nextPages, true));
        updateMoreNote();
        afterGrid();
        return added;
      })
      .catch((err) => {
        moreBusy = false;
        link.removeAttribute('aria-busy');
        throw err;
      });
  }

  function onMore(link) {
    loadMore(link)
      .then((added) => {
        if (!added) return;
        const saved = readMore() || { pages: 1 };
        writeMore({ pages: (saved.pages || 1) + 1, y: window.scrollY });
        const first = added[0] && $('.hc-card__link', added[0]);
        if (first) first.focus();
        const live = $('[data-col-live]');
        const note = $('#ColPages .col-more__note');
        if (live && note) live.textContent = note.textContent.trim();
      })
      .catch(() => { window.location.href = link.href; });
  }

  // Back to a URL whose Load more pages were loaded: load them again, then return to the saved scroll position
  function restoreMore() {
    const saved = readMore();
    if (!saved || !(saved.pages > 1) || !grid()) return;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    let loaded = 1;
    const finish = () => {
      writeMore({ pages: loaded, y: saved.y || 0 });
      window.scrollTo(0, saved.y || 0);
      if ('scrollRestoration' in history) history.scrollRestoration = 'auto';
    };
    const step = () => {
      const link = $('#ColPages a[data-col-more]');
      if (loaded >= saved.pages || !link) { finish(); return; }
      loadMore(link).then(() => { loaded += 1; step(); }).catch(finish);
    };
    step();
  }

  /* ---------- Expert band: online status ---------- */
  const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  function isOpen(cfg, now) {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: cfg.tz, weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(now);
    const get = (t) => (parts.find((p) => p.type === t) || {}).value || '';
    const day = DAYS.indexOf(get('weekday')) + 1;
    const hm = get('hour') + ':' + get('minute');
    return cfg.days.indexOf(day) !== -1 && hm >= cfg.open && hm < cfg.close;
  }

  function updateOnline() {
    $$('[data-col-expert]').forEach((band) => {
      const status = $('[data-col-online]', band);
      if (!status) return;
      let cfg = null;
      try { cfg = JSON.parse(band.dataset.hours || ''); } catch (e) { cfg = null; }
      const valid = cfg && cfg.tz && Array.isArray(cfg.days) && /^\d\d:\d\d$/.test(cfg.open || '') && /^\d\d:\d\d$/.test(cfg.close || '');
      let on = false;
      if (valid) { try { on = isOpen(cfg, new Date()); } catch (e) { on = false; } }
      status.hidden = !on;
    });
  }

  /* ---------- Events ---------- */
  document.addEventListener('change', (e) => {
    const el = e.target;
    if (!(el instanceof HTMLElement)) return;
    if (el.matches('[data-col-sort]')) {
      schedule(el);
      return;
    }
    if (el.form && el.form.id === 'ColFilterForm') {
      if (el.matches(RANGE)) {
        // Handle released: apply through the matching price box, same path as typing a price
        const p = rangeParts(el);
        const box = p && (el === p.lo ? p.loText : p.hiText);
        if (box) applyNow(box);
        return;
      }
      if (el.matches('[data-col-price]')) { syncRangeFromText(el); applyNow(el); }
      else schedule(el);
    }
  });

  document.addEventListener('input', (e) => {
    const el = e.target;
    if (el instanceof HTMLInputElement && el.matches(RANGE)) onRangeInput(el);
  });

  // Enter in a price field (or the hidden submit button) submits the form: apply instead of reloading
  document.addEventListener('submit', (e) => {
    if (e.target && e.target.id === 'ColFilterForm') {
      e.preventDefault();
      applyNow(document.activeElement);
    }
  });

  document.addEventListener('click', (e) => {
    const t = e.target instanceof Element ? e.target : null;
    if (!t) return;

    const more = t.closest('a[data-col-more]');
    if (more && shop.contains(more)) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      if (more.getAttribute('aria-busy') !== 'true') onMore(more);
      return;
    }

    // The "Showing …" note and the bar load more too (same path and busy guard); the link stays the only control
    const moreArea = t.closest('.col-more__note, .col-more__bar');
    if (moreArea && shop.contains(moreArea)) {
      const box = moreArea.closest('.col-more');
      const btn = box && $('a[data-col-more]', box);
      if (btn && btn.getAttribute('aria-busy') !== 'true') onMore(btn);
      return;
    }

    const link = t.closest('a[data-col-link]');
    if (link && shop.contains(link)) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      e.preventDefault();
      clearTimeout(timer);
      const isPage = link.hasAttribute('data-col-page');
      load(link.href, { push: true, scroll: isPage ? 'always' : true });
      return;
    }

    if (t.closest('[data-col-sheet-open]')) { openSheet(); return; }
    if (t.closest('[data-col-sheet-close]')) { closeSheet(); return; }

    const chat = t.closest('[data-open-chat]');
    if (chat) {
      // Expert band: 'viewed' question naming the models it lists (data-chat-models, set by assets/collection-compare.js), else 'collection'
      const band = chat.closest('[data-col-expert]');
      if (typeof window.openTidio === 'function') {
        if (!band) window.openTidio();
        else if (band.dataset.chatModels) window.openTidio('viewed', { models: band.dataset.chatModels });
        else window.openTidio('collection');
      }
      else if (window.tidioChatApi && typeof window.tidioChatApi.open === 'function') window.tidioChatApi.open();
    }
  });

  document.addEventListener('keydown', (e) => {
    const s = sheet();
    if (!s || !s.classList.contains('is-open')) return;
    if (e.key === 'Escape') { e.preventDefault(); closeSheet(); }
    else if (e.key === 'Tab') trapFocus(e);
  });

  const onBreakpoint = () => { if (!phone.matches) closeSheet(); };
  if (phone.addEventListener) phone.addEventListener('change', onBreakpoint);
  else if (phone.addListener) phone.addListener(onBreakpoint);

  history.replaceState(Object.assign({}, history.state, { colShop: true }), '');
  window.addEventListener('popstate', (e) => {
    if (!e.state || !e.state.colShop) return;
    load(window.location.pathname + window.location.search, { push: false, scroll: true });
  });

  updateOnline();
  setInterval(updateOnline, 5 * 60 * 1000);

  // Collection grid (each step does nothing without #product-grid[data-grid], e.g. on the search page)
  afterGrid();
  restoreMore();
  // Keep the scroll position for Back from a product, once Load more has been used on this URL
  window.addEventListener('pagehide', () => {
    const saved = grid() && readMore();
    if (saved) writeMore({ pages: saved.pages, y: window.scrollY });
  });
})();
